import { Table, TableColumns } from '@bp/ui-components';
import { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type TransferCurriculaRow = {
  uuid: string;
  subjectName: string;
  classNames: string;
  typeLabels: string[];
  isEpochPlan: boolean;
  personsCount: number;
  elective: boolean;
  teachingLoadEnabled: boolean;
  existing: number;
  possible: number;
  // Nur für den ExpertFilter (nicht sichtbar).
  grade: string;
  gradeGroup: string;
};

export const isCurriculumComplete = (row: TransferCurriculaRow) => row.existing >= row.possible;

type TransferCurriculaListProps = {
  rows: TransferCurriculaRow[];
  loading: boolean;
  // Ohne Selektion wird die Liste als reine View gerendert (z.B. bereits übertragene Sollstunden).
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
};

export const TransferCurriculaList = ({
  rows,
  loading,
  rowSelection,
  onRowSelectionChange,
}: TransferCurriculaListProps) => {
  const { t } = useTranslation();

  const columns: TableColumns<TransferCurriculaRow>[] = useMemo(
    () => [
      {
        header: t('subject.name'),
        id: 'subjectName',
        accessorKey: 'subjectName',
        size: 250,
      },
      {
        header: t('classes.title', { count: 2 }),
        id: 'classNames',
        accessorKey: 'classNames',
        size: 150,
      },
      {
        header: t('common.type'),
        id: 'type',
        size: 150,
        accessorFn: (row) => (row.isEpochPlan ? t('epochs.plan') : row.typeLabels.join(', ')),
      },
      {
        header: t('persons.title', { count: 2 }),
        id: 'personsCount',
        accessorKey: 'personsCount',
        type: 'number',
        size: 100,
      },
      {
        header: t('lesson.basics.isElective'),
        id: 'elective',
        accessorKey: 'elective',
        type: 'boolean',
        size: 120,
      },
      {
        header: t('lesson.basics.isTeachingLoadEnabled.short'),
        id: 'teachingLoadEnabled',
        accessorKey: 'teachingLoadEnabled',
        type: 'boolean',
        size: 120,
      },
      {
        header: t('lesson.allLessonsInSchoolYear'),
        id: 'lessons',
        size: 150,
        alignment: 'right',
        accessorFn: (row) => `${row.existing} ${t('common.fromCount', { count: row.possible })}`,
      },
      // Nur für den ExpertFilter verfügbar, per columnVisibility ausgeblendet.
      {
        header: t('classes.level.full'),
        id: 'grade',
        accessorKey: 'grade',
        meta: { filterName: t('classes.level.full') },
      },
      {
        header: t('gradeGroups.title', { count: 1 }),
        id: 'gradeGroup',
        accessorKey: 'gradeGroup',
        meta: { filterName: t('gradeGroups.title', { count: 1 }) },
      },
    ],
    [t],
  );

  return (
    <Table<TransferCurriculaRow>
      data={rows}
      columns={columns}
      columnVisibility={{ grade: false, gradeGroup: false }}
      loading={loading}
      canScroll={true}
      fillParent={true}
      showSelect={!!onRowSelectionChange}
      showSelected={!!onRowSelectionChange}
      selectOnClick={!!onRowSelectionChange}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      isOnWhite={true}
      showActionBar
      actionBarSettings={{ showSelectedRowsCount: !!onRowSelectionChange, showExpertFilter: true }}
      emptyStateSettings={{ title: t('lesson.transfer.empty') }}
    />
  );
};
